import { ControlledDateField, ControlledSelectField } from 'components/Form';
import { ControlledTextField } from 'components/Form/ControlledTextField';
import { GroupHeader } from 'components/GroupHeader';
import { ModalDialog } from 'components/ModalDialog';
import type { BimWorkOrderVersionSeasonDistributionData } from 'gql/graphql';
import { cnMixSpace, Grid, GridItem, IconCalendar } from 'uikit';
import { formatNumberToRu } from 'utils';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

interface IBimWorkOrderVersionRequisitesReadProps {
    defaultValues?: any;
    onCancel: () => void;
}

export const BimWorkOrderVersionRequisitesRead: React.FC<IBimWorkOrderVersionRequisitesReadProps> = (props) => {
    const { defaultValues = {}, onCancel } = props;

    const transformDefaultValue: any = useMemo(
        () => ({
            ...defaultValues,
            rangeDate: [defaultValues.startDate, defaultValues.finishDate],
            amountWithTax: defaultValues.amountWithTax === null ? 0 : formatNumberToRu(defaultValues.amountWithTax, 2),
            amountWithoutTax:
                defaultValues.amountWithoutTax === null ? 0 : formatNumberToRu(defaultValues.amountWithoutTax, 2),
            tax: defaultValues.tax === null ? 0 : formatNumberToRu(defaultValues.tax, 2),
            remainder: defaultValues.remainder === null ? 0 : formatNumberToRu(defaultValues.remainder, 2),
        }),
        [defaultValues],
    );

    const { control, handleSubmit } = useForm({
        defaultValues: transformDefaultValue,
    });

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    return (
        <ModalDialog
            title="Данные Наряд-Заказа"
            isOpen
            onConfirm={handleCancel}
            onClose={handleCancel}
            withTitleCloseButton
            btnConfirmDisabled
        >
            <form onSubmit={handleSubmit(handleCancel)}>
                <Grid cols={1} style={{ width: 600, padding: 20 }}>
                    <GridItem>
                        <GroupHeader mB="s">Основная информация</GroupHeader>
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            name="status"
                            items={[defaultValues.status]}
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Предмет"
                            name="subject"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Внутренний номер"
                            name="internalNumber"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Подрядчик"
                            name="contractor"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledDateField
                            control={control}
                            label="Дата выпуска"
                            name="signingDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            required
                            disabled
                        />
                        <GroupHeader mB="s">Данные Изменения к НЗ</GroupHeader>
                        {!!defaultValues?.workOrderChangeNumber && (
                            <ControlledTextField
                                control={control}
                                label="Номер Изм. к НЗ"
                                name="workOrderChangeNumber"
                                disabled
                                className={cnMixSpace({ mB: 's' })}
                            />
                        )}
                        <ControlledDateField
                            control={control}
                            label="Дата подписания"
                            name="workOrderChangeSigningDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled
                        />
                        {!!defaultValues?.seasonDistributions?.length && (
                            <>
                                <GroupHeader mB="s">% перераспределения Итогов</GroupHeader>

                                {(defaultValues?.seasonDistributions as BimWorkOrderVersionSeasonDistributionData[])
                                    .slice()
                                    .sort(({ year: yearA }, { year: yearB }) => yearA - yearB)
                                    .map((year) => (
                                        <ControlledTextField
                                            key={year.workOrderVersionSeasonId}
                                            control={control}
                                            placeholder="%"
                                            type="text"
                                            label={`${year.year}`}
                                            name={`season_${year.workOrderVersionSeasonId}`}
                                            className={cnMixSpace({ mB: 's' })}
                                            disabled
                                        />
                                    ))}
                            </>
                        )}
                        <GroupHeader mB="s">Сумма Наряд-Заказа</GroupHeader>
                        <ControlledTextField
                            control={control}
                            label="Сумма с НДС (руб.)"
                            name="amountWithTax"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Сумма без НДС (руб.)"
                            name="amountWithoutTax"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="НДС, 20% (руб.)"
                            name="tax"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Остаток без НДС (руб.)"
                            name="remainder"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled
                        />
                        <GroupHeader mB="s">Производство работ</GroupHeader>
                        <ControlledDateField
                            control={control}
                            label="Дата начала и окончания"
                            name="rangeDate"
                            className={cnMixSpace({ mB: 'l' })}
                            type="date-range"
                            required
                            rightSide={[IconCalendar, IconCalendar]}
                            disabled
                        />
                    </GridItem>
                </Grid>
            </form>
        </ModalDialog>
    );
};

BimWorkOrderVersionRequisitesRead.displayName = 'BimWorkOrderVersionRequisitesRead';
